import React from "react";
import PicCard from "./PicCard";
import classNames from "classnames";
import PICCardSkeleton from "./PICCardSkeleton";
import {
	TDepartmentID,
	TDepartmentWiseAssignment,
} from "redux/app/vdmdashboard/@types";

interface IDepartmentPosition {
	id: string;
	title: string;
}

interface IDepartmentConfig {
	id: string;
	title: string;
	isDisabled?: boolean;
	positions: IDepartmentPosition[];
}

const departmentConfig: IDepartmentConfig[] = [
	{
		id: "technical",
		title: "Technical",
		positions: [
			{ id: "manager", title: "Manager" },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive" },
		],
	},
	{
		id: "operations_commercial",
		title: "Operations/Commercial",
		positions: [
			{ id: "manager", title: "Manager" },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive" },
		],
	},
	{
		id: "marine_hr",
		title: "Marine HR",
		positions: [
			{ id: "manager", title: "Manager" },
			{ id: "executive", title: "Executive" },
		],
	},
	{
		id: "sqv_inspections",
		title: "SQV/Inspections",
		positions: [
			{ id: "manager", title: "Manager" },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive" },
		],
	},
];

type ViewUsersProps = {
	vesselId: string;
	isLoading: boolean;
	activeDepartment: TDepartmentID;
	setActiveDepartment: React.Dispatch<React.SetStateAction<TDepartmentID>>;
	localAssignment: TDepartmentWiseAssignment;
	localPicturePreviews?: Record<string, string>;
};

function ViewPIC(props: ViewUsersProps) {
	const {
		localAssignment: departmentWiseAssignments,
		isLoading,
		setActiveDepartment,
		activeDepartment,
		localPicturePreviews,
	} = props;

	const transformAssignmentToPicCard = (assignment: any) => {
		if (!assignment || Object.keys(assignment).length === 0) return null;

		// A picture just picked in AssignUsers will be shown for the user.
		const previewUrl = assignment.id
			? localPicturePreviews?.[assignment.id]
			: undefined;

		return {
			department: "",
			name: assignment.name,
			email: assignment.email,
			contact: assignment.phone_number,
			profilePicture: previewUrl
				? previewUrl
				: assignment.profile_picture
					? `https://console.wayship.io${assignment.profile_picture}`
					: undefined,
		};
	};

	const renderDepartmentPICs = (departmentId: string) => {
		const department = departmentConfig.find((d) => d.id === departmentId);

		if (isLoading) {
			const count = department?.positions.length ?? 3;
			return (
				<div className="mt-4 flex flex-wrap gap-4 justify-center">
					{Array.from({ length: count }).map((_, i) => (
						<PICCardSkeleton key={i} />
					))}
				</div>
			);
		}

		if (
			!department ||
			!departmentWiseAssignments ||
			!(departmentWiseAssignments as Record<string, any>)[departmentId]
		)
			return null;

		const assignments = (departmentWiseAssignments as Record<string, any>)[
			departmentId
		];
		const positionCards = department.positions.map((position) => {
			const assignment = assignments[position.id];
			const picData = transformAssignmentToPicCard(assignment);
			if (!assignment || Object.keys(assignment).length === 0) {
				return null;
			}

			if (!picData) return null;

			return (
				<div key={position.id} className="min-w-[250px]">
					<PicCard
						pic={{
							...picData,
							department: `${department.title} ${position.title}`,
						}}
					/>
				</div>
			);
		});

		const visibleCards = positionCards.filter((card) => card !== null);

		if (visibleCards.length === 0) {
			return (
				<div className="mt-4 py-8 text-center text-sm text-slate-400">
					No one assigned in {department.title} yet.
				</div>
			);
		}

		return (
			<div className="mt-4 flex flex-wrap gap-4 justify-center">
				{visibleCards}
			</div>
		);
	};

	return (
		<div className="w-full mt-4 gap-x-4 pb-4 px-4 rounded mb-10">
			<div className="flex overflow-x-auto mb-4 border-b border-gray-200 gap-1">
				{departmentConfig.map((tab) => {
					const { id, title, isDisabled } = tab;
					return (
						<button
							key={id}
							className={classNames(
								"px-3 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200",
								{
									"text-blue-600 bg-blue-50 border-b-2 border-blue-600":
										activeDepartment === id,
									"text-gray-700 hover:bg-gray-100 border-b-2 border-transparent":
										activeDepartment !== id && !isDisabled,
									"text-gray-600 cursor-not-allowed": isDisabled || isLoading,
								},
							)}
							onClick={() => {
								if (isDisabled || isLoading) return;
								setActiveDepartment(id as TDepartmentID);
							}}
							disabled={isDisabled || isLoading}
						>
							{title}
						</button>
					);
				})}
			</div>

			<div>{renderDepartmentPICs(activeDepartment)}</div>
		</div>
	);
}

export default ViewPIC;
